import React, { useState, useEffect, useRef } from 'react';
import ModalPortal from './ModalPortal';
import GenericAddModal from './GenericAddModal';
import DataTableDetailModal from './DataTableDetailModal';
import Tooltip from '../common/Tooltip';

interface TreeNode {
  id: string;
  name: string;
  type: 'organization' | 'department' | 'team' | 'process' | 'document';
  status: 'Active' | 'Inactive' | 'Draft' | 'Review';
  children?: TreeNode[];
  metadata?: {
    owner?: string;
    created?: string;
    count?: number;
    description?: string;
  };
}

const DataTreeCrudTest: React.FC = () => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['item-1', 'item-1.1', 'item-1.2', 'item-2', 'item-2.1', 'item-3', 'item-3.1', 'item-4', 'item-4.1', 'item-5', 'item-5.1']));
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [parentNodeForAdd, setParentNodeForAdd] = useState<string | null>(null);
  const [selectedTreeNode, setSelectedTreeNode] = useState<TreeNode | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check and update scroll state when content changes
  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Only update if there's a meaningful difference
      const needsScroll = scrollHeight > clientHeight + 5; // 5px buffer
      
      // Set overflow based on content size
      container.style.overflowY = needsScroll ? 'scroll' : 'auto';

      // Update debug info
      const heightEl = document.getElementById('debug-height');
      const scrollHeightEl = document.getElementById('debug-scrollHeight');
      const clientHeightEl = document.getElementById('debug-clientHeight');
      const overflowEl = document.getElementById('debug-overflow');
      const hasScrollbarEl = document.getElementById('debug-hasScrollbar');

      if (heightEl) heightEl.textContent = `${container.offsetHeight}px`;
      if (scrollHeightEl) scrollHeightEl.textContent = `${scrollHeight}px`;
      if (clientHeightEl) clientHeightEl.textContent = `${clientHeight}px`;
      if (overflowEl) overflowEl.textContent = container.style.overflowY;
      if (hasScrollbarEl) hasScrollbarEl.textContent = needsScroll ? 'YES' : 'NO';
    }
  };

  // Set up effect to check scroll on mount and content changes
  useEffect(() => {
    // Initial check
    updateScrollState();
    
    // Set up a debounced check for any layout changes
    const checkScroll = () => {
      if (scrollCheckTimeoutRef.current) {
        clearTimeout(scrollCheckTimeoutRef.current);
      }
      scrollCheckTimeoutRef.current = setTimeout(updateScrollState, 100);
    };

    // Listen for window resize as it might affect container height
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('resize', checkScroll);
      if (scrollCheckTimeoutRef.current) {
        clearTimeout(scrollCheckTimeoutRef.current);
      }
    };
  }, []);

  // Check scroll when expanded nodes change
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      updateScrollState();
    });
  }, [expandedNodes]);

  const [treeData, setTreeData] = useState<TreeNode[]>([
    {
      id: 'item-1',
      name: 'Item 1',
      type: 'organization',
      status: 'Active',
      metadata: { owner: 'System', created: '2025-01-01', count: 25 },
      children: [
        {
          id: 'item-1.1',
          name: 'Item 1.1',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User A', created: '2025-01-15', count: 12 },
          children: [
            {
              id: 'item-1.1.1',
              name: 'Item 1.1.1',
              type: 'team',
              status: 'Active',
              metadata: { owner: 'User B', created: '2025-02-01', count: 5 },
              children: [
                { id: 'item-1.1.1.1', name: 'Item 1.1.1.1', type: 'process', status: 'Active', metadata: { owner: 'User C', created: '2025-02-10' } },
                { id: 'item-1.1.1.2', name: 'Item 1.1.1.2', type: 'process', status: 'Review', metadata: { owner: 'User D', created: '2025-02-15' } }
              ]
            },
            {
              id: 'item-1.1.2',
              name: 'Item 1.1.2',
              type: 'team',
              status: 'Active',
              metadata: { owner: 'User E', created: '2025-02-05', count: 4 },
              children: [
                { id: 'item-1.1.2.1', name: 'Item 1.1.2.1', type: 'process', status: 'Active', metadata: { owner: 'User F', created: '2025-02-20' } },
                { id: 'item-1.1.2.2', name: 'Item 1.1.2.2', type: 'document', status: 'Draft', metadata: { owner: 'User G', created: '2025-02-25' } }
              ]
            }
          ]
        },
        {
          id: 'item-1.2',
          name: 'Item 1.2',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User H', created: '2025-01-20', count: 8 },
          children: [
            {
              id: 'item-1.2.1',
              name: 'Item 1.2.1',
              type: 'team',
              status: 'Active',
              metadata: { owner: 'User I', created: '2025-02-08', count: 6 },
              children: [
                { id: 'item-1.2.1.1', name: 'Item 1.2.1.1', type: 'process', status: 'Active', metadata: { owner: 'User J', created: '2025-02-12' } },
                { id: 'item-1.2.1.2', name: 'Item 1.2.1.2', type: 'document', status: 'Active', metadata: { owner: 'User K', created: '2025-02-18' } }
              ]
            }
          ]
        },
        {
          id: 'item-1.3',
          name: 'Item 1.3',
          type: 'department',
          status: 'Review',
          metadata: { owner: 'User L', created: '2025-01-25', count: 3 }
        }
      ]
    },
    {
      id: 'item-2',
      name: 'Item 2',
      type: 'organization',
      status: 'Active',
      metadata: { owner: 'System', created: '2025-01-05', count: 18 },
      children: [
        {
          id: 'item-2.1',
          name: 'Item 2.1',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User M', created: '2025-01-10', count: 10 },
          children: [
            {
              id: 'item-2.1.1',
              name: 'Item 2.1.1',
              type: 'team',
              status: 'Inactive',
              metadata: { owner: 'User N', created: '2025-01-15', count: 7 },
              children: [
                { id: 'item-2.1.1.1', name: 'Item 2.1.1.1', type: 'process', status: 'Active', metadata: { owner: 'User O', created: '2025-01-20' } },
                { id: 'item-2.1.1.2', name: 'Item 2.1.1.2', type: 'document', status: 'Draft', metadata: { owner: 'User P', created: '2025-01-25' } }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'item-3',
      name: 'Item 3',
      type: 'organization',
      status: 'Active',
      metadata: { owner: 'System', created: '2025-01-10', count: 15 },
      children: [
        {
          id: 'item-3.1',
          name: 'Item 3.1',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User AA', created: '2025-01-20', count: 8 },
          children: [
            { id: 'item-3.1.1', name: 'Item 3.1.1', type: 'team', status: 'Active', metadata: { owner: 'User BB', created: '2025-02-01', count: 3 } },
            { id: 'item-3.1.2', name: 'Item 3.1.2', type: 'team', status: 'Review', metadata: { owner: 'User CC', created: '2025-02-05', count: 5 } }
          ]
        }
      ]
    },
    {
      id: 'item-4',
      name: 'Item 4',
      type: 'organization',
      status: 'Inactive',
      metadata: { owner: 'System', created: '2025-01-12', count: 20 },
      children: [
        {
          id: 'item-4.1',
          name: 'Item 4.1',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User DD', created: '2025-01-22', count: 12 },
          children: [
            { id: 'item-4.1.1', name: 'Item 4.1.1', type: 'team', status: 'Active', metadata: { owner: 'User EE', created: '2025-02-10', count: 6 } },
            { id: 'item-4.1.2', name: 'Item 4.1.2', type: 'team', status: 'Draft', metadata: { owner: 'User FF', created: '2025-02-15', count: 6 } }
          ]
        },
        {
          id: 'item-4.2',
          name: 'Item 4.2',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User GG', created: '2025-01-25', count: 8 }
        }
      ]
    },
    {
      id: 'item-5',
      name: 'Item 5',
      type: 'organization',
      status: 'Review',
      metadata: { owner: 'System', created: '2025-01-15', count: 25 },
      children: [
        {
          id: 'item-5.1',
          name: 'Item 5.1',
          type: 'department',
          status: 'Active',
          metadata: { owner: 'User HH', created: '2025-01-28', count: 15 },
          children: [
            { id: 'item-5.1.1', name: 'Item 5.1.1', type: 'team', status: 'Active', metadata: { owner: 'User II', created: '2025-02-20', count: 8 } },
            { id: 'item-5.1.2', name: 'Item 5.1.2', type: 'team', status: 'Active', metadata: { owner: 'User JJ', created: '2025-02-25', count: 7 } }
          ]
        }
      ]
    }
  ]);

  const toggleExpanded = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleNodeSelect = (nodeId: string) => {
    // Find the selected node from tree data
    const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const node = findNodeById(treeData, nodeId);
    if (node) {
      setSelectedTreeNode(node);
    }
  };

  const handleAddNew = (data: Record<string, string>) => {
    // For scaffolding purposes - just close the modal
    // In real implementation, would add to tree data
    console.log('Adding new item with data:', data);
  };

  const getNodeIcon = (type: string, hasChildren: boolean, isExpanded: boolean) => {
    if (hasChildren) {
      return isExpanded ? '📂' : '📁';
    }
    switch (type) {
      case 'organization': return '🏢';
      case 'department': return '🏛️';
      case 'team': return '👥';
      case 'process': return '⚙️';
      case 'document': return '📄';
      default: return '📋';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Active': return 'visual-status-success';
      case 'Draft': return 'visual-status-warning';
      case 'Review': return 'visual-status-info';
      case 'Inactive': return 'visual-status-inactive';
      default: return 'visual-status-info';
    }
  };

  const renderTreeNode = (node: TreeNode, level: number = 0): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode === node.id;
    const paddingLeft = level * 24 + 12;

    return (
      <div key={node.id}>
        <div
          className={`interactive-tree-node ${isSelected ? 'selected' : ''}`}
          style={{
            paddingLeft: `${paddingLeft}px`,
            backgroundColor: isSelected ? '#e0f2fe' : undefined,
            borderLeft: level > 0 ? '1px solid #e5e7eb' : undefined
          }}
          onClick={() => handleNodeSelect(node.id)}
        >
          <div className="layout-tree-node-row">
            {hasChildren && (
              <button
                className="btn-tree-expand"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(node.id);
                }}
                style={{ marginRight: '4px' }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            {!hasChildren && <span style={{ width: '16px', marginRight: '4px' }} />}
            
            <span style={{ marginRight: '8px', fontSize: '16px' }}>
              {getNodeIcon(node.type, !!hasChildren, isExpanded)}
            </span>
            
            <div className="layout-tree-node-content">
              <span className="text-heading-small" style={{ flex: 1 }}>
                {node.name}
              </span>
              
              <span className="text-code-mono" style={{ fontSize: '10px', textTransform: 'uppercase', color: '#6b7280' }}>
                {node.type}
              </span>
              
              <span className={getStatusClass(node.status)}>
                {node.status}
              </span>
              
              {node.metadata?.count && (
                <span className="text-label-muted" style={{ fontSize: '11px' }}>
                  ({node.metadata.count})
                </span>
              )}
            </div>

            <div className="layout-tree-node-actions">
              <button
                className="btn-icon-only"
                onClick={(e) => {
                  e.stopPropagation();
                  setParentNodeForAdd(node.id);
                  setShowAddModal(true);
                }}
                title="Add Child"
                style={{ fontSize: '12px' }}
              >
                ➕
              </button>
              <button
                className="btn-icon-only"
                onClick={(e) => {
                  e.stopPropagation();
                  // Edit functionality
                }}
                title="Edit"
                style={{ fontSize: '12px' }}
              >
                ✏️
              </button>
              <button
                className="btn-icon-only"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(node.id);
                }}
                title="Delete"
                style={{ fontSize: '12px' }}
              >
                🗑️
              </button>
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="layout-tree-children">
            {node.children!.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const infoAnchorRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div>
      <div className="css-info">
        <div>Hierarchical Tree CRUD Pattern</div>
        <button
          ref={infoAnchorRef}
          className="css-info-icon"
          aria-label="CSS info"
          title="CSS info"
          aria-expanded={tooltipVisible}
          aria-controls="css-tooltip-tree"
          onClick={(e) => { e.stopPropagation(); setTooltipVisible(v => !v); }}
        >
          <span className="sr-only">CSS info</span>
        </button>
        <Tooltip anchorRef={infoAnchorRef} isOpen={tooltipVisible} onClose={() => setTooltipVisible(false)} id="css-tooltip-tree">
          CSS Classes Used:
          .container-tree-view - Tree container
          .interactive-tree-node - Clickable tree nodes
          .layout-tree-children - Nested children container
          .btn-tree-expand - Expand/collapse buttons
          .visual-status-success/.visual-status-warning - Status indicators
          .text-heading-small/.text-code-mono - Typography
          .layout-flex-row - Horizontal layouts
          .btn-icon-only - Action buttons
        </Tooltip>
      </div>

      {/* Tree Controls */}
      <div className="container-toolbar">
        <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="layout-flex-row" style={{ gap: '12px' }}>
            <button 
              className="btn-secondary-ghost"
              onClick={() => setExpandedNodes(new Set())}
            >
              📁 Collapse All
            </button>
            <button 
              className="btn-secondary-ghost"
              onClick={() => {
                // Expand all logic
                const getAllNodeIds = (nodes: TreeNode[]): string[] => {
                  let ids: string[] = [];
                  nodes.forEach(node => {
                    ids.push(node.id);
                    if (node.children) {
                      ids = ids.concat(getAllNodeIds(node.children));
                    }
                  });
                  return ids;
                };
                setExpandedNodes(new Set(getAllNodeIds(treeData)));
              }}
            >
              📂 Expand All
            </button>
            <div className="control-search-field">
              <input type="text" placeholder="Search tree..." />
            </div>
          </div>
          <button 
            className="btn-add-primary"
            onClick={() => {
              setParentNodeForAdd(null);
              setShowAddModal(true);
            }}
          >
            ➕ Add New
          </button>
        </div>
      </div>

      {/* Tree Container - Scrollable (Removed outer container-tree-view wrapper) */}
      <div 
        ref={scrollContainerRef} 
        className="layout-table-scroll-container" 
        style={{ 
          flex: '1 1 auto', // Take all available space in parent
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0, // Allow flex shrinking
          maxHeight: 'calc(100vh - 250px)', // Reserve space for UI elements
          background: '#ffffff', // White background from container-tree-view
          border: '1px solid #e5e7eb', // Border from container-tree-view
          borderRadius: '8px', // Border radius from container-tree-view
          padding: '16px' // Padding from container-tree-view
        }}
      >
        {treeData.map(node => renderTreeNode(node))}
      </div>

      {/* Debug Footer */}
      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        background: '#f3f4f6', 
        border: '1px solid #d1d5db', 
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace'
      }}>
        <div><strong>Debug Info:</strong></div>
        <div>Tree nodes: {treeData.length}</div>
        <div>Expanded nodes: {expandedNodes.size}</div>
        <div>Scroll container height: <span id="debug-height">calculating...</span></div>
        <div>Scroll container scrollHeight: <span id="debug-scrollHeight">calculating...</span></div>
        <div>Scroll container clientHeight: <span id="debug-clientHeight">calculating...</span></div>
        <div>Overflow Y: <span id="debug-overflow">calculating...</span></div>
        <div>Container has scrollbar: <span id="debug-hasScrollbar">calculating...</span></div>
      </div>

      {/* Add New Modal */}
      <GenericAddModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddNew}
        title="Add New Item"
      />

      {/* Delete Confirmation Modal */}
      <ModalPortal isOpen={!!showDeleteModal} onClose={() => setShowDeleteModal(null)}>
        <div className="modal-header">
          <h3>Confirm Delete</h3>
          <button 
            className="modal-close-button" 
            onClick={() => setShowDeleteModal(null)}
          >
            ×
          </button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to delete this node and all its children? This action cannot be undone.</p>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setShowDeleteModal(null)}>
            Cancel
          </button>
          <button className="btn-danger">
            Delete
          </button>
        </div>
      </ModalPortal>

      {/* Detail Modal for Selected Tree Node */}
      <DataTableDetailModal
        isOpen={!!selectedTreeNode}
        onClose={() => setSelectedTreeNode(null)}
        selectedTreeNode={selectedTreeNode}
      />
    </div>
  );
};

export default DataTreeCrudTest;